import * as React from 'react';
import { peekInvite, portalCall, setActiveClient, signInGoogle, signInMicrosoft, signOut } from './auth';
import { BrandLockup, GoogleMark, Icon, MicrosoftMark, SeltzerArt } from './brand';
import { Link } from './Shell';

// /invite?c=<organization>&t=<token> - the link in an invitation email.
// Shows who invited them and to what, has them sign in with the invited account, then accepts and opens the apps.

interface IPeek { clientName: string; email: string; invitedBy: string; roleName: string; expired: boolean; }

const Invite: React.FunctionComponent<{ signedIn: boolean; googleClientId: string | null }> = ({ signedIn, googleClientId }) => {
  const q: URLSearchParams = new URLSearchParams(window.location.search);
  const c: string = q.get('c') || '';
  const t: string = q.get('t') || '';
  const returnTo: string = `/invite?c=${encodeURIComponent(c)}&t=${encodeURIComponent(t)}`;
  const [info, setInfo] = React.useState<IPeek | null>(null);
  const [error, setError] = React.useState<string>('');
  const [accepting, setAccepting] = React.useState<boolean>(false);

  React.useEffect((): void => {
    peekInvite(c, t).then(setInfo).catch((e: Error): void => setError(e.message));
  }, []);

  // Signed in: accept straight away, then open the organization's apps.
  React.useEffect((): void => {
    if (!signedIn || info === null || info.expired || accepting) { return; }
    setAccepting(true);
    portalCall<{ tenantId: string }>('POST', 'invites/accept', { tenantId: c, token: t })
      .then((r: { tenantId: string }): void => { setActiveClient(r.tenantId); window.location.assign('/apps'); })
      .catch((e: Error): void => { setError(e.message); });
  }, [signedIn, info]);

  let body: React.ReactElement;
  if (error !== '') {
    body = (
      <React.Fragment>
        <h1>This invitation can't be used</h1>
        <div className="bu-alert bu-alert-danger" style={{ marginBottom: 18 }}>{error}</div>
        {signedIn
          ? <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}><button className="bu-btn bu-btn-secondary" onClick={signOut}><Icon name="logout" size={16} /> Sign out</button><Link to="/apps" className="bu-btn bu-btn-ghost">Go to my apps</Link></div>
          : <Link to="/signin" className="bu-btn bu-btn-secondary">Go to sign in</Link>}
      </React.Fragment>
    );
  } else if (info === null || (signedIn && !info.expired)) {
    body = <React.Fragment><h1>{signedIn && info !== null ? `Joining ${info.clientName}...` : 'Opening your invitation...'}</h1><p>One moment.</p></React.Fragment>;
  } else if (info.expired) {
    body = (
      <React.Fragment>
        <h1>This invitation has expired</h1>
        <p>Ask {info.invitedBy || `someone at ${info.clientName}`} to send you a new one.</p>
      </React.Fragment>
    );
  } else {
    body = (
      <React.Fragment>
        <p className="bu-eyebrow" style={{ marginBottom: 6 }}>You're invited</p>
        <h1>Join {info.clientName}</h1>
        <p>
          {info.invitedBy ? <span><strong>{info.invitedBy}</strong> invited you</span> : 'You were invited'} to work in {info.clientName} on BeefedUp Apps
          {info.roleName ? <span> as <strong>{info.roleName}</strong></span> : ''}.
        </p>
        <div className="bu-alert bu-alert-info" style={{ marginBottom: 18 }}>
          <Icon name="key" size={18} /><span>Sign in as <strong>{info.email}</strong>. The invitation only works for that account.</span>
        </div>
        <button className="bu-idp" onClick={(): void => signInMicrosoft(returnTo)}>
          <MicrosoftMark size={22} /><span>Continue with Microsoft</span><em>Microsoft 365</em>
        </button>
        {googleClientId !== null && (
          <button className="bu-idp" onClick={(): void => { void signInGoogle(googleClientId, returnTo); }}>
            <GoogleMark size={22} /><span>Continue with Google</span><em>Workspace</em>
          </button>
        )}
        <p className="bu-signin-legal">Personal Microsoft and Gmail accounts aren't supported. By signing in you agree to the <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>.</p>
      </React.Fragment>
    );
  }

  return (
    <div className="bu-signin">
      <aside className="bu-signin-art">
        <Link to="/" className="bu-brand" style={{ color: '#fff' }}><BrandLockup size={34} /></Link>
        <div>
          <div style={{ marginBottom: 36 }}><SeltzerArt height={150} /></div>
          <blockquote>
            The right tools for your team, with your company's data kept private to your company.
            <small>BeefedUp IT</small>
          </blockquote>
        </div>
        <div style={{ display: 'flex', gap: 22, color: 'var(--graphite-400)', fontSize: 13, fontWeight: 600 }}>
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><Icon name="lock" size={16} /> Encrypted</span>
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><Icon name="database" size={16} /> Isolated per organization</span>
        </div>
      </aside>
      <main className="bu-signin-form"><div className="bu-signin-box">{body}</div></main>
    </div>
  );
};

export default Invite;
